import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/hooks/useAuth';
import { useDashboard } from '@/hooks/useDashboard';
import { useCapabilities } from '@/hooks/useCapabilities';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Activity, Sparkles, RefreshCw, Users, Calendar, Mic, FileText, Clock } from 'lucide-react';
import QuickRecordingModal from '@/components/QuickRecordingModal';
import ConnectedModeDashboard from '@/components/ConnectedModeDashboard';
import { useRecordingSessionStore } from '@/stores/recordingSessionStore';
import { InterruptedRecordingBanner } from '@/components/recording/RecordingRecoveryModal';
import { UsageLimitBanner } from '@/components/subscriptions/UsageLimitBanner';
import { DataRetentionBanner } from '@/components/shared/DataRetentionBanner';
import { useSubscriptionStore } from '@/stores/subscriptionStore';
import { SubscriptionStatusBar } from '@/components/shared/SubscriptionStatusBar';

// ============================================================================
// Status Styles (consistent with Encounters module)
// ============================================================================

const APPOINTMENT_STATUS_STYLES: Record<string, { dot: string; bg: string; text: string; border: string }> = {
  'Completed': { dot: 'bg-emerald-400', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-400' },
  'In Progress': { dot: 'bg-blue-400', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-400' },
  'Scheduled': { dot: 'bg-gray-300', bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-300' },
  'Cancelled': { dot: 'bg-red-400', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-400' },
  'Pending': { dot: 'bg-amber-400', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-400' },
};

const TRANSCRIPTION_STATUS_STYLES: Record<string, { dot: string; bg: string; text: string }> = {
  'Ready': { dot: 'bg-emerald-400', bg: 'bg-emerald-50', text: 'text-emerald-700' },
  'Processing': { dot: 'bg-amber-400', bg: 'bg-amber-50', text: 'text-amber-700' },
  'Failed': { dot: 'bg-red-400', bg: 'bg-red-50', text: 'text-red-700' },
  'Pending': { dot: 'bg-gray-300', bg: 'bg-gray-100', text: 'text-gray-600' },
};

function getAppointmentStatusStyle(status: string) {
  return APPOINTMENT_STATUS_STYLES[status] || APPOINTMENT_STATUS_STYLES['Scheduled'];
}

function getTranscriptionStatusStyle(status: string) {
  return TRANSCRIPTION_STATUS_STYLES[status] || TRANSCRIPTION_STATUS_STYLES['Pending'];
}

// ============================================================================
// Skeleton Loading
// ============================================================================

function DashboardSkeleton() {
  return (
    <div className="space-y-6 w-full animate-pulse">
      {/* Header skeleton */}
      <div className="bg-white rounded-xl border border-gray-200 p-8">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 bg-gray-200 rounded-2xl" />
          <div className="space-y-2 flex-1">
            <div className="h-7 bg-gray-200 rounded w-64" />
            <div className="h-4 bg-gray-100 rounded w-48" />
          </div>
        </div>
        <div className="h-4 bg-gray-100 rounded w-80" />
      </div>

      {/* Stat cards skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <div className="h-4 bg-gray-100 rounded w-28" />
                <div className="h-8 bg-gray-200 rounded w-16" />
              </div>
              <div className="w-12 h-12 bg-gray-200 rounded-xl" />
            </div>
            <div className="mt-4 h-4 bg-gray-100 rounded w-32" />
          </div>
        ))}
      </div>

      {/* Content skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6">
          <div className="h-5 bg-gray-200 rounded w-40 mb-6" />
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                <div className="w-1 h-12 bg-gray-200 rounded-full" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-36" />
                  <div className="h-3 bg-gray-100 rounded w-48" />
                </div>
                <div className="h-6 bg-gray-200 rounded-full w-20" />
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="h-5 bg-gray-200 rounded w-28 mb-6" />
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 p-3">
                <div className="w-9 h-9 bg-gray-200 rounded-lg" />
                <div className="space-y-1.5 flex-1">
                  <div className="h-4 bg-gray-200 rounded w-32" />
                  <div className="h-3 bg-gray-100 rounded w-40" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Dashboard Component
// ============================================================================

const Dashboard: React.FC = () => {
  const { user, isReadOnly, canEditClinical } = useAuth();
  const { dashboardMode, capabilities, loading: capabilitiesLoading } = useCapabilities();
  const navigate = useNavigate();
  const [isQuickRecordingOpen, setIsQuickRecordingOpen] = useState(false);

  // Get interrupted recording sessions for recovery banner
  const { interruptedSessions } = useRecordingSessionStore();

  // Subscription status + personal usage for dashboard widgets
  const { fetchPersonalUsage, fetchSubscription } = useSubscriptionStore();
  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'operator') {
      fetchPersonalUsage();
      fetchSubscription();
    }
  }, [user?.role]);

  // Handle reviewing interrupted recordings
  const handleReviewInterrupted = () => {
    if (interruptedSessions.length > 0) {
      // Navigate to the most recent interrupted session
      const mostRecent = interruptedSessions.reduce((latest, session) =>
        session.lastActiveAt > latest.lastActiveAt ? session : latest
      );

      const params = new URLSearchParams({
        recover: 'true',
        sessionId: mostRecent.id,
      });

      if (mostRecent.appointmentId) {
        navigate(`/recording/${mostRecent.appointmentId}?${params.toString()}`);
      } else if (mostRecent.encounterId) {
        navigate(`/recording?encounterId=${mostRecent.encounterId}&${params.toString()}`);
      } else {
        navigate(`/recording?${params.toString()}`);
      }
    }
  };

  // Fetch dashboard data with auto-refresh every 5 minutes
  const {
    dashboardData,
    systemStatus,
    loading,
    error,
    refreshData,
    lastUpdated
  } = useDashboard(5 * 60 * 1000);

  // Unified provider homepage for BOTH modes. ConnectedModeDashboard now renders the
  // shared worklist and picks its data source (encounters vs appointments) by mode, and
  // shows its own loading skeleton while capabilities resolve. The legacy standalone
  // dashboard body below is retained for reference but no longer reached (follow-up: remove).
  return <ConnectedModeDashboard />;

  // Transform dashboard data for stats cards
  const stats = dashboardData ? [
    {
      name: 'Total Appointments',
      value: dashboardData.stats.totalAppointments.value,
      change: dashboardData.stats.totalAppointments.change,
      changeType: dashboardData.stats.totalAppointments.changeType,
      icon: Calendar,
    },
    {
      name: 'Active Patients',
      value: dashboardData.stats.activePatients.value,
      change: dashboardData.stats.activePatients.change,
      changeType: dashboardData.stats.activePatients.changeType,
      icon: Users,
    },
    {
      name: 'Transcriptions Today',
      value: dashboardData.stats.transcriptionsToday.value,
      change: dashboardData.stats.transcriptionsToday.change,
      changeType: dashboardData.stats.transcriptionsToday.changeType,
      icon: FileText,
    },
    {
      name: 'Recording Time',
      value: dashboardData.stats.recordingTime.value,
      change: dashboardData.stats.recordingTime.change,
      changeType: dashboardData.stats.recordingTime.changeType,
      icon: Clock,
    },
  ] : [];

  // Use real recent appointments data
  const recentAppointments = dashboardData?.recentAppointments || [];

  // Show loading state
  if (loading && !dashboardData) {
    return <DashboardSkeleton />;
  }

  // Show error state
  if (error && !dashboardData) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6">
        <h3 className="text-sm font-semibold text-red-800 mb-2">
          Error loading dashboard
        </h3>
        <p className="text-sm text-red-700 mb-4">{error}</p>
        <button
          onClick={refreshData}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-xl transition-colors"
        >
          <RefreshCw size={16} />
          Try again
        </button>
      </div>
    );
  }

  return (
    <motion.div
      className="space-y-6 w-full overflow-x-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Welcome Header - Clinical Design */}
      <motion.div
        className="clinical-card-static p-8 relative overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Glass morphism background - Clinical palette */}
        <div className="absolute inset-0 bg-gradient-to-br from-[rgba(30,74,122,0.03)] to-[rgba(22,160,133,0.05)]" />

        <div className="relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="bg-white w-16 h-16 rounded-2xl flex items-center justify-center">
                  <img src="/images/amplify-favicon.png" alt="Amplify" className="w-full h-full object-contain p-2" />
                </div>
                <div>
                  <h1 className="text-3xl font-display font-bold text-[var(--color-clinical-navy)]">
                    Welcome back, {user?.firstName}!
                  </h1>
                  <div className="flex items-center gap-2 text-[var(--color-clinical-blue)]">
                    <Sparkles className="text-[var(--color-surgical-teal)]" size={16} />
                    <span>Healthcare Audio Transcription Platform</span>
                  </div>
                </div>
              </div>
              <p className="text-[var(--color-clinical-blue)] opacity-80 leading-relaxed">
                Here's an overview of your clinical workflow activity
                {lastUpdated && (
                  <span className="ml-2 text-sm opacity-60">
                    • Last updated {lastUpdated.toLocaleTimeString()}
                  </span>
                )}
              </p>
            </motion.div>

            <motion.div
              className="flex flex-col sm:flex-row gap-3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <motion.button
                onClick={() => setIsQuickRecordingOpen(true)}
                disabled={isReadOnly()}
                className={`btn-clinical-primary ${
                  isReadOnly()
                    ? '!bg-gray-400 cursor-not-allowed opacity-60'
                    : ''
                }`}
                whileHover={isReadOnly() ? {} : { scale: 1.05 }}
                whileTap={isReadOnly() ? {} : { scale: 0.95 }}
                title={isReadOnly() ? 'Read-only access - Recording not permitted' : 'Start a new recording'}
              >
                <Mic size={18} />
                Start Recording
              </motion.button>

              <motion.button
                onClick={refreshData}
                disabled={loading}
                className="flex items-center gap-2 bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 font-medium px-6 py-3 rounded-xl shadow-clinical-soft transition-all duration-200 disabled:opacity-50"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <motion.div
                  animate={{ rotate: loading ? 360 : 0 }}
                  transition={{ duration: 1, repeat: loading ? Infinity : 0, ease: "linear" }}
                >
                  <RefreshCw size={18} />
                </motion.div>
                Refresh
              </motion.button>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Subscription Status Bar */}
      <SubscriptionStatusBar />

      {/* Interrupted Recording Banner - Shows when there are interrupted recordings */}
      {interruptedSessions.length > 0 && (
        <InterruptedRecordingBanner
          sessionCount={interruptedSessions.length}
          onReview={handleReviewInterrupted}
        />
      )}

      {/* Usage Limit Banner - Shows when approaching or exceeding subscription limits */}
      {(user?.role === 'admin' || user?.role === 'operator') && (
        <UsageLimitBanner />
      )}

      {/* Data Retention Banner - Shows for connected mode orgs with expiring encounters */}
      <DataRetentionBanner />

      {/* Stats Cards */}
      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
      >
        {stats.map((stat, index) => {
          const IconComponent = stat.icon;

          return (
            <motion.div
              key={stat.name}
              className="clinical-card p-6 group"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 * index }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[var(--color-clinical-blue)] opacity-80 mb-2">{stat.name}</p>
                  <p className="stat-number">{stat.value}</p>
                </div>
                <div className="flex-shrink-0">
                  <motion.div
                    className="icon-vital icon-container-md"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <IconComponent className="text-white" size={24} />
                  </motion.div>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <motion.span
                  className={`text-sm font-medium px-2 py-1 rounded-lg ${
                    stat.changeType === 'positive'
                      ? 'text-[var(--color-surgical-teal)] bg-[rgba(22,160,133,0.1)]'
                      : 'text-[var(--color-accent-coral)] bg-[rgba(255,107,107,0.1)]'
                  }`}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2 + 0.1 * index }}
                >
                  {stat.change}
                </motion.span>
                <span className="text-sm text-[var(--color-clinical-blue)] opacity-60">from last month</span>
              </div>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Appointments */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-clinical-soft overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-200">
            <h3 className="text-lg font-display font-semibold text-[var(--color-clinical-navy)]">Recent Appointments</h3>
          </div>
          <div className="p-5">
            <div className="space-y-3">
              {recentAppointments.length > 0 ? (
                recentAppointments.map((appointment) => {
                  const statusStyle = getAppointmentStatusStyle(appointment.status);
                  const transcriptionStyle = getTranscriptionStatusStyle(appointment.transcriptionStatus);

                  return (
                    <div
                      key={appointment.id}
                      className={`flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-clinical-soft transition-all duration-200 cursor-pointer border-l-4 ${statusStyle.border}`}
                      onClick={() => navigate(`/appointments/${appointment.id}`)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{appointment.patient}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{appointment.type} • Dr. {appointment.provider}</p>
                          </div>
                          <div className="text-left sm:text-right flex-shrink-0">
                            <p className="text-sm font-medium text-gray-900">{new Date(appointment.date).toLocaleDateString()}</p>
                            <p className="text-xs text-gray-500">{appointment.time}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                        {/* Status badge with dot */}
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${statusStyle.bg} ${statusStyle.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`} />
                          {appointment.status}
                        </span>
                        {/* Transcription status with dot */}
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${transcriptionStyle.bg} ${transcriptionStyle.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${transcriptionStyle.dot}`} />
                          {appointment.transcriptionStatus}
                        </span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-12">
                  <div className="mx-auto w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
                    <Calendar className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-sm font-semibold text-gray-900">No recent appointments</p>
                  <p className="text-sm text-gray-500 mt-1">Appointments will appear here once scheduled</p>
                  {!isReadOnly() && (
                    <button
                      onClick={() => navigate('/appointments/new')}
                      className="mt-4 px-5 py-2.5 text-sm font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
                    >
                      Schedule your first appointment
                    </button>
                  )}
                </div>
              )}
            </div>
            {capabilities?.ui.showAppointments && (
              <div className="mt-5 pt-4 border-t border-gray-100">
                <button
                  onClick={() => navigate('/appointments')}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
                >
                  View all appointments →
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-clinical-soft overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-200">
            <h3 className="text-lg font-display font-semibold text-[var(--color-clinical-navy)]">Quick Actions</h3>
          </div>
          <div className="p-4 space-y-1">
            <button
              onClick={() => !isReadOnly() && setIsQuickRecordingOpen(true)}
              disabled={isReadOnly()}
              className={`w-full flex items-center gap-3 p-3 text-left rounded-xl transition-all duration-200 border ${
                isReadOnly()
                  ? 'opacity-50 cursor-not-allowed bg-gray-50 border-gray-200'
                  : 'hover:bg-gray-50 border-transparent hover:border-gray-200 hover:shadow-sm'
              }`}
              title={isReadOnly() ? 'Read-only access - Action not permitted' : ''}
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                isReadOnly() ? 'bg-gray-100' : 'bg-blue-50'
              }`}>
                <Mic className={`w-4.5 h-4.5 ${isReadOnly() ? 'text-gray-400' : 'text-blue-600'}`} />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">Start New Recording</p>
                <p className="text-xs text-gray-500">Begin audio transcription</p>
              </div>
            </button>

            <button
              onClick={() => !isReadOnly() && navigate('/patients/new')}
              disabled={isReadOnly()}
              className={`w-full flex items-center gap-3 p-3 text-left rounded-xl transition-all duration-200 border ${
                isReadOnly()
                  ? 'opacity-50 cursor-not-allowed bg-gray-50 border-gray-200'
                  : 'hover:bg-gray-50 border-transparent hover:border-gray-200 hover:shadow-sm'
              }`}
              title={isReadOnly() ? 'Read-only access - Action not permitted' : ''}
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                isReadOnly() ? 'bg-gray-100' : 'bg-emerald-50'
              }`}>
                <Users className={`w-4.5 h-4.5 ${isReadOnly() ? 'text-gray-400' : 'text-emerald-600'}`} />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">Add New Patient</p>
                <p className="text-xs text-gray-500">Register patient information</p>
              </div>
            </button>

            <button
              onClick={() => !isReadOnly() && navigate('/appointments/new')}
              disabled={isReadOnly()}
              className={`w-full flex items-center gap-3 p-3 text-left rounded-xl transition-all duration-200 border ${
                isReadOnly()
                  ? 'opacity-50 cursor-not-allowed bg-gray-50 border-gray-200'
                  : 'hover:bg-gray-50 border-transparent hover:border-gray-200 hover:shadow-sm'
              }`}
              title={isReadOnly() ? 'Read-only access - Action not permitted' : ''}
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                isReadOnly() ? 'bg-gray-100' : 'bg-amber-50'
              }`}>
                <Calendar className={`w-4.5 h-4.5 ${isReadOnly() ? 'text-gray-400' : 'text-amber-600'}`} />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">Schedule Appointment</p>
                <p className="text-xs text-gray-500">Book patient visit</p>
              </div>
            </button>

            <button
              onClick={() => navigate('/appointments')}
              className="w-full flex items-center gap-3 p-3 text-left rounded-xl transition-all duration-200 border border-transparent hover:bg-gray-50 hover:border-gray-200 hover:shadow-sm"
            >
              <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                <FileText className="w-4.5 h-4.5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">View Transcripts</p>
                <p className="text-xs text-gray-500">Access completed transcriptions</p>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* System Status */}
      <motion.div
        className="bg-white rounded-xl border border-gray-200 shadow-clinical-soft p-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.0 }}
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-7 h-7 rounded-lg bg-teal-50 border border-teal-100 flex items-center justify-center">
            <Activity className="text-[var(--color-surgical-teal)]" size={14} />
          </div>
          <h3 className="text-lg font-display font-semibold text-[var(--color-clinical-navy)]">System Status</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {systemStatus ? (
            <>
              <div className="flex items-center gap-4 p-4 bg-gray-50/50 rounded-xl border border-gray-100">
                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                  systemStatus.transcriptionService.status === 'online' ? 'bg-emerald-400' :
                  systemStatus.transcriptionService.status === 'degraded' ? 'bg-amber-400' : 'bg-red-400'
                }`} />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">Transcription Service</p>
                  <p className="text-xs text-gray-500 truncate">
                    {systemStatus.transcriptionService.message}
                    {systemStatus.transcriptionService.pendingJobs !== undefined && (
                      <span className="ml-1">({systemStatus.transcriptionService.pendingJobs} pending)</span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 bg-gray-50/50 rounded-xl border border-gray-100">
                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                  systemStatus.database.status === 'online' ? 'bg-emerald-400' : 'bg-red-400'
                }`} />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">Database</p>
                  <p className="text-xs text-gray-500 truncate">{systemStatus.database.message}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 bg-gray-50/50 rounded-xl border border-gray-100">
                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                  systemStatus.encryption.status === 'online' ? 'bg-emerald-400' : 'bg-red-400'
                }`} />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">Encryption</p>
                  <p className="text-xs text-gray-500 truncate">{systemStatus.encryption.message}</p>
                </div>
              </div>
            </>
          ) : (
            <div className="col-span-3 text-center py-8">
              <div className="mx-auto w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center mb-3">
                <RefreshCw className="text-gray-400 animate-spin" size={20} />
              </div>
              <p className="text-sm text-gray-500">Loading system status...</p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Quick Recording Modal */}
      <QuickRecordingModal
        isOpen={isQuickRecordingOpen}
        onClose={() => setIsQuickRecordingOpen(false)}
      />
    </motion.div>
  );
};

export default Dashboard;
